'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '@/lib/utils'
import { BREAKPOINTS, type Breakpoint } from '@/lib/constants'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'

const TabsContext = React.createContext<{
  value?: string
  onValueChange?: (value: string) => void
}>({})

const TabsListContext = React.createContext<{
  registerTab: (value: string, el: HTMLButtonElement | null) => void
}>({
  registerTab: () => {},
})

function Tabs({
  className,
  value,
  onValueChange,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsContext.Provider value={{ value, onValueChange }}>
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn('flex flex-col gap-2', className)}
        value={value}
        onValueChange={onValueChange}
        {...props}
      />
    </TabsContext.Provider>
  )
}

function TabsList({
  className,
  dropdownBreakpoint = 'sm',
  mobileDropdown = true,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  dropdownBreakpoint?: Breakpoint
  /** When true (default), use dropdown on small screens. When false, always show pill form. Future: may allow per-screen-size. */
  mobileDropdown?: boolean
}) {
  const { value: activeValue, onValueChange } = React.useContext(TabsContext)
  const [activeTabBounds, setActiveTabBounds] = React.useState<{
    width: number
    left: number
  } | null>(null)
  const [isMobile, setIsMobile] = React.useState(false)
  const tabsListRef = React.useRef<HTMLDivElement>(null)
  const tabRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map())

  const updateActiveTabBounds = React.useCallback(() => {
    if (!tabsListRef.current || !activeValue) return
    const activeTab = tabRefs.current.get(activeValue)
    if (activeTab) {
      const bounds = activeTab.getBoundingClientRect()
      const parentBounds = tabsListRef.current.getBoundingClientRect()
      setActiveTabBounds({
        width: bounds.width,
        left: bounds.left - parentBounds.left,
      })
    }
  }, [activeValue])

  const registerTab = React.useCallback(
    (tabValue: string, el: HTMLButtonElement | null) => {
      if (el) {
        tabRefs.current.set(tabValue, el)
      } else {
        tabRefs.current.delete(tabValue)
      }
    },
    [],
  )

  const contextValue = React.useMemo(() => ({ registerTab }), [registerTab])

  React.useEffect(() => {
    if (!mobileDropdown) {
      setIsMobile(false)
      return
    }

    const mediaQuery = BREAKPOINTS[dropdownBreakpoint]
    const checkMobile = () => {
      if (typeof window !== 'undefined' && window.matchMedia) {
        setIsMobile(window.matchMedia(mediaQuery).matches)
      } else {
        setIsMobile(false)
      }
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [dropdownBreakpoint, mobileDropdown])

  // Update bounds on initial render and when active value changes
  // useLayoutEffect ensures refs are set before we read them
  React.useLayoutEffect(() => {
    updateActiveTabBounds()
  }, [activeValue, updateActiveTabBounds])

  // Watch for container size changes (e.g., flex layout adjustments)
  React.useEffect(() => {
    if (!tabsListRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      updateActiveTabBounds()
    })

    resizeObserver.observe(tabsListRef.current)
    return () => resizeObserver.disconnect()
  }, [updateActiveTabBounds])

  // Extract tab information for mobile select
  const tabs = React.Children.toArray(props.children)
    .filter(
      (
        child,
      ): child is React.ReactElement<{
        value: string
        children: React.ReactNode
      }> => React.isValidElement(child),
    )
    .map(child => ({
      value: child.props.value,
      label: child.props.children,
    }))

  if (mobileDropdown && isMobile) {
    return (
      <div className="w-full">
        <Select value={activeValue} onValueChange={onValueChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {tabs.map(tab => (
              <SelectItem key={tab.value} value={tab.value}>
                {tab.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  return (
    <TabsListContext.Provider value={contextValue}>
      <TabsPrimitive.List
        ref={tabsListRef}
        data-slot="tabs-list"
        className={cn(
          'relative bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px] gap-1',
          className,
        )}
        {...props}
      >
        <div
          className="absolute bg-background rounded-md transition-all duration-300 ease-in-out shadow-[2px_2px_4px_rgba(0,0,0,0.08)]"
          style={{
            width: activeTabBounds?.width ?? 0,
            height: 'calc(100% - 6px)',
            left: activeTabBounds?.left ?? 0,
            top: '3px',
          }}
        />
        {props.children}
      </TabsPrimitive.List>
    </TabsListContext.Provider>
  )
}

function TabsTrigger({
  className,
  value,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { registerTab } = React.useContext(TabsListContext)

  const setRef = React.useCallback(
    (el: HTMLButtonElement | null) => {
      registerTab(value, el)
    },
    [registerTab, value],
  )

  return (
    <TabsPrimitive.Trigger
      ref={setRef}
      data-slot="tabs-trigger"
      value={value}
      className={cn(
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-muted-foreground inline-flex h-[calc(100%-1px)] items-center justify-center gap-1.5 rounded-md border border-transparent px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors duration-300 ease-in-out focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground data-[state=active]:font-semibold relative [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
